import type { MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircleOutlined,
  ErrorOutline,
  PublishedWithChanges,
  PublishedWithChangesOutlined,
  Share,
} from '@mui/icons-material';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { ServerError } from '@/types/errors/server.error';
import ConfirmationModal from '@/components/common/confirmation-modal';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { useEventStore } from '@/state/stores/use-event-store';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useFlowMode } from '@/components/flows/detail/use-flow-mode';
import { getObjectId } from '@/helpers/object.id.helper';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { usePublishBatchMutation } from '@/state/mutations/events/use-publish-batch';
import type { BatchPublishItem } from '@/types/dtos/events/request/batch-publish.dto';
import { PUBLICATION_ITEM_TYPE } from '@/types/dtos/events/request/batch-publish.dto';
import { PUBLICATION_STATE_QUERY_KEY, usePublicationStateQuery } from '@/state/queries/events/use-publication-state';
import {
  useFlowPublishStateForFlowDetail,
  type PublicationStatePhase,
} from '@/components/events/detail/tabs/flows/grid/actions/use-flow-publish-state-for-flow-detail';
import { useFlowBasicsSlotPublishState } from '@/components/events/detail/tabs/flows/grid/actions/use-flow-basics-slot-publish-state';
import type { PublicationStateItemDto } from '@/types/dtos/events/response/publication-state.dto';
import { useBreakpointDown } from '@/hooks/use-breakpoint';

type PublishFormData = {
  publishEventInformation: boolean;
  publishFlowTimeWindow: boolean;
  publishSlotSequence: boolean;
  publishSessionsInTheFlow: boolean;
  publishLibrarySessions: boolean;
  /** Flow schedule tab: publish session basics for all sessions placed in the flow (one scope). */
  publishFlowSlotSessions: boolean;
  /** Flow basics widget (event-slot context): FLOW_BASIC */
  publishBasicsFlow: boolean;
  /** Flow basics widget (event-slot context): EVENT_SLOT */
  publishBasicsEventSlot: boolean;
};

function sessionNeedsSessionBasicPublish(
  session: SessionDto,
  publicationReady: boolean,
  publicationItems: PublicationStateItemDto[] | undefined,
): boolean {
  if (!publicationReady || !publicationItems) {
    return false;
  }
  const r = publicationItems.find(
    (i) => i.itemType === PUBLICATION_ITEM_TYPE.SESSION_BASIC && i.scopeKey === session.id,
  );
  if (r) return r.isUpToDate !== true;
  return true;
}

type PublishContext = 'flowDetail' | 'flowDetailBasics' | 'flowGrid';

export const Publish = ({
  flow,
  buttonOrIcon = 'icon',
  context = 'flowGrid',
  flowPlanOverride,
  onIndicativeClick,
}: {
  flow: FlowDto;
  /** `outlinedSmall`: same chrome as slot-list ActionButton (outlined, info, small, ~22px). */
  buttonOrIcon?: 'button' | 'icon' | 'outlinedSmall';
  context?: PublishContext;
  flowPlanOverride?: FlowDto | null;
  onIndicativeClick?: () => void;
}) => {
  const t = useTranslations();
  const isCompactSlotChrome = useBreakpointDown('sm');
  const notification = useNotifications();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { mutateAsync: publishBatchMutation, isPending: isPublishBatchPending } = usePublishBatchMutation();
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventOrganize = useEventStore((s) => s.eventOrganize);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const isSingleFlowEvent = event && !event.multiFlow;
  const { data: permissions } = usePermissionsQuery();

  // Get eventSlotId from URL - this is the explicit eventSlot we're working with
  const eventSlotIdParam = searchParams.get('eventSlotId');

  // IMPORTANT: Always use flow from store when available (has correct flow.slot based on eventSlotIdParam)
  // When in flow detail context, the store flow has the correct eventSlot set
  // The store is the SINGLE SOURCE OF TRUTH for the eventSlot context
  // Fall back to flow prop only if store doesn't have flow or it's a different flow
  const flowFromStore = useFlowStore((s) => s.flow);

  // Use store flow if it exists and matches the prop flow ID
  // The store flow will have the correct flow.slot set based on eventSlotIdParam from URL
  // This ensures we always use the correct eventSlot context, not the first one from flow prop
  // IMPORTANT: When flowFromStore has a slot, always prefer it (it's the correct context)
  // Even if flowFromStore.slot.id === flow.slot.id, prefer flowFromStore because it has the latest version/publishedModes
  const flowToUse = flowFromStore && flowFromStore.id === flow.id ? flowFromStore : flow;

  // Determine which eventSlotId to use: explicit from URL param, or from flow.slot
  const eventSlotIdToUse = eventSlotIdParam || flowToUse.slot?.id;
  const isEventSlotView = !!eventSlotIdParam;
  const flowMode = useFlowMode(
    flowToUse.origin ? (typeof flowToUse.origin === 'string' ? flowToUse.origin : flowToUse.origin.id) : flowToUse.id,
  );
  const eventMode = useEventMode();
  const modeToUse = context === 'flowDetail' || context === 'flowDetailBasics' ? flowMode : eventMode;
  const isPlanMode = modeToUse === 'PLAN';
  const isFlowDetailContext = context === 'flowDetail';
  const isFlowDetailBasicsContext = context === 'flowDetailBasics';
  const isIndicativeOnly = isFlowDetailContext || isFlowDetailBasicsContext ? false : isOrganizeMode(eventMode);
  const flowPlanFromStore = useFlowStore((s) => s.flowPlan);
  const flowPlan = flowPlanOverride ?? flowPlanFromStore;
  const planEventIdForPublication = eventPlan?.id ?? event?.id;
  const eventIdFromFlowParent =
    flowToUse.parent && typeof flowToUse.parent === 'object' && 'id' in flowToUse.parent
      ? flowToUse.parent.id
      : undefined;
  const eventId = eventPlan?.id ?? eventOrganize?.id ?? event?.id ?? eventIdFromFlowParent;
  const canUpdateEvent = !!eventId && !!permissions?.events?.[eventId]?.includes('update');

  const flowDetailPublicationQuery = usePublicationStateQuery({
    eventId: isFlowDetailContext && planEventIdForPublication ? planEventIdForPublication : undefined,
    mode: 'ORGANIZE',
    enabled: isFlowDetailContext && canUpdateEvent && Boolean(planEventIdForPublication),
  });
  const flowDetailPublishFromHook = useFlowPublishStateForFlowDetail(flowPlanOverride);

  // Resolve PLAN flow ID - no fallback, flowPlan is required
  const planFlowId = useMemo(() => {
    if (!flowPlan) return null;
    if (isFlowDetailContext || isFlowDetailBasicsContext) {
      const planOriginId = flowPlan.origin ? getObjectId(flowPlan.origin) : flowPlan.id;
      const flowOriginId = flowToUse.origin ? getObjectId(flowToUse.origin) : flowToUse.id;
      return planOriginId === flowOriginId ? flowPlan.id : null;
    }
    return flowPlan.id;
  }, [flowPlan, flowToUse, isFlowDetailContext, isFlowDetailBasicsContext]);

  const flowForPublish = useMemo(() => {
    if (!flowPlan) return null;
    if (!isFlowDetailContext && !isFlowDetailBasicsContext) return flowPlan;
    const planOriginId = flowPlan.origin ? getObjectId(flowPlan.origin) : flowPlan.id;
    const flowOriginId = flowToUse.origin ? getObjectId(flowToUse.origin) : flowToUse.id;
    return planOriginId === flowOriginId ? flowPlan : null;
  }, [flowPlan, flowToUse, isFlowDetailContext, isFlowDetailBasicsContext]);

  const eventSlotForPublish = useMemo(() => {
    if (!flowPlan) return null;
    if (!eventSlotIdParam) {
      return isFlowDetailContext || isFlowDetailBasicsContext ? flowForPublish?.slot : flowPlan.slot;
    }
    const matchSlot = (slotId?: string) =>
      slotId ? eventPlan?.slots?.find((slot) => slot.id === slotId || slot.origin === slotId) : undefined;
    if (isFlowDetailContext || isFlowDetailBasicsContext) {
      const flowSlot = flowForPublish?.slot;
      if (eventSlotIdParam && flowSlot) {
        const originId = flowSlot.origin ? getObjectId(flowSlot.origin) : undefined;
        const matchesUrl =
          flowSlot.id === eventSlotIdParam || flowSlot.origin === eventSlotIdParam || originId === eventSlotIdParam;
        // After flow sync, nested event slot `version` updates on the flow before `eventPlan.slots` is refetched.
        if (matchesUrl) {
          return flowSlot;
        }
      }
      return (
        matchSlot(eventSlotIdParam) ?? matchSlot(flowForPublish?.slot?.id) ?? matchSlot(flowForPublish?.slot?.origin)
      );
    }
    const planSlot = matchSlot(eventSlotIdParam) ?? matchSlot(flowPlan.slot?.id) ?? matchSlot(flowPlan.slot?.origin);
    const flowSlot = flowToUse.slot;
    if (!planSlot) return flowSlot;
    if (!flowSlot) return planSlot;
    const planPublished = planSlot.publishedModes?.find((pm) => pm.mode === 'ORGANIZE');
    const flowPublished = flowSlot.publishedModes?.find((pm) => pm.mode === 'ORGANIZE');
    const publishedChanged =
      planPublished?.version !== flowPublished?.version || planPublished?.publishedAt !== flowPublished?.publishedAt;
    return publishedChanged ? flowSlot : planSlot;
  }, [
    eventSlotIdParam,
    eventPlan?.slots,
    flowForPublish?.slot,
    flowPlan,
    flowToUse.slot,
    isFlowDetailContext,
    isFlowDetailBasicsContext,
  ]);

  const basicsPublishEnabled =
    isFlowDetailBasicsContext && Boolean(flowPlan && planFlowId && planEventIdForPublication);
  const basicsPublishFromHook = useFlowBasicsSlotPublishState({
    enabled: basicsPublishEnabled && canUpdateEvent,
    planEventId: planEventIdForPublication,
    planFlowId,
    flowPlan: flowForPublish,
    planEventSlot: eventSlotForPublish ?? undefined,
  });

  const showFlowTimeWindow = isEventSlotView && canUpdateEvent;
  const showSessionsOption = canUpdateEvent;

  const gridPublicationQuery = usePublicationStateQuery({
    eventId: !isFlowDetailContext && !isFlowDetailBasicsContext && eventId ? eventId : undefined,
    mode: 'ORGANIZE',
    enabled: !isFlowDetailContext && !isFlowDetailBasicsContext && canUpdateEvent && Boolean(eventId),
  });
  const isGridPublicationReady = gridPublicationQuery.isPublicationStateReady;

  // Event publish state for single-flow events
  const eventToUse = eventPlan ?? eventOrganize ?? event;
  const eventUpToDate = useMemo(() => {
    if (!isSingleFlowEvent || !eventToUse) return true;
    if (!canUpdateEvent) return true;
    if (isGridPublicationReady && gridPublicationQuery.data) {
      const pid = gridPublicationQuery.data.planEventId;
      const rec = gridPublicationQuery.data.items.find(
        (i) => i.itemType === PUBLICATION_ITEM_TYPE.EVENT_BASIC && i.scopeKey === pid,
      );
      return rec?.isUpToDate === true;
    }
    return true;
  }, [isSingleFlowEvent, eventToUse, canUpdateEvent, isGridPublicationReady, gridPublicationQuery.data]);

  const resolveSessionBySlot = (slot: FlowDto['slots'][number]): SessionDto | undefined => {
    if (slot.contentType !== 'Session' || !slot.content) return undefined;
    if (typeof slot.content === 'object') return slot.content as SessionDto;
    const slotContentId = slot.content;
    const planSession = eventSessionsPlan?.find((s) => s.id === slotContentId);
    const organizeSession = eventSessionsOrganize?.find((s) => s.id === slotContentId);
    return planSession ?? organizeSession;
  };

  const sessionsInFlow = useMemo(() => {
    if (!flowPlan) return [];
    const slots = (isFlowDetailContext || isFlowDetailBasicsContext ? flowForPublish?.slots : flowPlan.slots) ?? [];
    const sessions = slots.map((slot) => resolveSessionBySlot(slot)).filter(Boolean) as SessionDto[];
    const resolvePublishSession = (session: SessionDto) => {
      const originId = session.origin ? getObjectId(session.origin) : session.id;
      const planSession = eventSessionsPlan?.find((s) => {
        const planOriginId = s.origin ? getObjectId(s.origin) : s.id;
        return planOriginId === originId || s.id === originId;
      });
      return planSession ?? session;
    };
    return sessions.map(resolvePublishSession);
  }, [
    flowPlan,
    flowForPublish?.slots,
    eventSessionsPlan,
    eventSessionsOrganize,
    isFlowDetailContext,
    isFlowDetailBasicsContext,
  ]);

  const uniqueSessionsInFlow = useMemo(() => {
    const byId = new Map<string, SessionDto>();
    for (const s of sessionsInFlow) {
      byId.set(s.id, s);
    }
    return [...byId.values()];
  }, [sessionsInFlow]);

  const flowSpecificSessions = useMemo(
    () => sessionsInFlow.filter((session) => !session.isInLibrary),
    [sessionsInFlow],
  );
  const librarySessions = useMemo(() => sessionsInFlow.filter((session) => session.isInLibrary), [sessionsInFlow]);
  const hasLibrarySessions = librarySessions.length > 0;

  const flowSpecificSessionsUpToDate = useMemo(() => {
    if (!flowSpecificSessions.length) return false;
    if (isGridPublicationReady && gridPublicationQuery.data) {
      const items = gridPublicationQuery.data.items;
      return flowSpecificSessions.every((session) => {
        const r = items.find((i) => i.itemType === PUBLICATION_ITEM_TYPE.SESSION_BASIC && i.scopeKey === session.id);
        return r?.isUpToDate === true;
      });
    }
    if (canUpdateEvent && !isFlowDetailContext && !isFlowDetailBasicsContext) {
      return true;
    }
    return false;
  }, [
    flowSpecificSessions,
    isGridPublicationReady,
    gridPublicationQuery.data,
    canUpdateEvent,
    isFlowDetailContext,
    isFlowDetailBasicsContext,
  ]);
  const librarySessionsUpToDate = useMemo(() => {
    if (!librarySessions.length) return false;
    if (isGridPublicationReady && gridPublicationQuery.data) {
      const items = gridPublicationQuery.data.items;
      return librarySessions.every((session) => {
        const r = items.find((i) => i.itemType === PUBLICATION_ITEM_TYPE.SESSION_BASIC && i.scopeKey === session.id);
        return r?.isUpToDate === true;
      });
    }
    if (canUpdateEvent && !isFlowDetailContext && !isFlowDetailBasicsContext) {
      return true;
    }
    return false;
  }, [
    librarySessions,
    isGridPublicationReady,
    gridPublicationQuery.data,
    canUpdateEvent,
    isFlowDetailContext,
    isFlowDetailBasicsContext,
  ]);

  const legacyPublishState = useMemo(() => {
    const isGridContext = !isFlowDetailContext && !isFlowDetailBasicsContext;

    if (isFlowDetailContext || isFlowDetailBasicsContext) {
      return {
        publicationPhase: 'disabled' as PublicationStatePhase,
        isUpToDate: false,
        needsInitialPublish: true,
        needsRepublish: false,
        flowUpToDate: false,
        eventSlotUpToDate: false,
        eventUpToDate: true,
        sessionsInFlowUpToDate: true,
      };
    }

    if (!flowPlan || !flowForPublish) {
      return {
        publicationPhase: 'disabled' as PublicationStatePhase,
        isUpToDate: false,
        needsInitialPublish: true,
        needsRepublish: false,
        flowUpToDate: false,
        eventSlotUpToDate: false,
        eventUpToDate: isSingleFlowEvent ? eventUpToDate : true,
        sessionsInFlowUpToDate: true,
      };
    }

    if (isGridContext && !canUpdateEvent) {
      return {
        publicationPhase: 'disabled' as PublicationStatePhase,
        isUpToDate: true,
        needsInitialPublish: false,
        needsRepublish: false,
        flowUpToDate: true,
        eventSlotUpToDate: true,
        eventUpToDate: true,
        sessionsInFlowUpToDate: true,
      };
    }

    if (isGridContext && canUpdateEvent && !eventId) {
      return {
        publicationPhase: 'disabled' as PublicationStatePhase,
        isUpToDate: true,
        needsInitialPublish: false,
        needsRepublish: false,
        flowUpToDate: true,
        eventSlotUpToDate: true,
        eventUpToDate: true,
        sessionsInFlowUpToDate: true,
      };
    }

    if (isGridContext && canUpdateEvent && gridPublicationQuery.isError) {
      return {
        publicationPhase: 'error' as PublicationStatePhase,
        isUpToDate: false,
        needsInitialPublish: false,
        needsRepublish: false,
        flowUpToDate: false,
        eventSlotUpToDate: false,
        eventUpToDate: true,
        sessionsInFlowUpToDate: true,
      };
    }

    if (isGridContext && canUpdateEvent && !isGridPublicationReady) {
      return {
        publicationPhase: 'loading' as PublicationStatePhase,
        isUpToDate: false,
        needsInitialPublish: false,
        needsRepublish: false,
        flowUpToDate: false,
        eventSlotUpToDate: false,
        eventUpToDate: true,
        sessionsInFlowUpToDate: true,
      };
    }

    if (isGridContext && isGridPublicationReady && gridPublicationQuery.data) {
      const items = gridPublicationQuery.data.items;
      const pid = gridPublicationQuery.data.planEventId;

      const flowSlotsRec =
        planFlowId != null
          ? items.find((i) => i.itemType === PUBLICATION_ITEM_TYPE.FLOW_SLOTS && i.scopeKey === planFlowId)
          : undefined;
      const flowUpToDatePub = flowSlotsRec?.isUpToDate === true;

      let eventSlotUpToDatePub = true;
      let eventSlotRec: (typeof items)[number] | undefined;
      if (eventSlotForPublish) {
        const sk = `${pid}:eventSlot:${eventSlotForPublish.id}`;
        eventSlotRec = items.find((i) => i.itemType === PUBLICATION_ITEM_TYPE.EVENT_SLOT && i.scopeKey === sk);
        eventSlotUpToDatePub = eventSlotRec?.isUpToDate === true;
      }

      const sessionsOutOfSync =
        showSessionsOption && (!flowSpecificSessionsUpToDate || (hasLibrarySessions && !librarySessionsUpToDate));
      const eventOutOfSync = isSingleFlowEvent && !eventUpToDate;
      const hasEventSlotContext = isEventSlotView && !!eventSlotForPublish;
      const isUpToDate = hasEventSlotContext
        ? flowUpToDatePub && eventSlotUpToDatePub && !sessionsOutOfSync && !eventOutOfSync
        : flowUpToDatePub && !sessionsOutOfSync && !eventOutOfSync;

      const needsInitialPublish = hasEventSlotContext ? !flowSlotsRec && !eventSlotRec : !flowSlotsRec;
      const needsRepublish = !needsInitialPublish && !isUpToDate;

      return {
        publicationPhase: 'ready' as PublicationStatePhase,
        isUpToDate,
        needsInitialPublish,
        needsRepublish,
        flowUpToDate: flowUpToDatePub,
        eventSlotUpToDate: eventSlotUpToDatePub,
        eventUpToDate: isSingleFlowEvent ? eventUpToDate : true,
        sessionsInFlowUpToDate: true,
      };
    }

    return {
      publicationPhase: 'disabled' as PublicationStatePhase,
      isUpToDate: true,
      needsInitialPublish: false,
      needsRepublish: false,
      flowUpToDate: true,
      eventSlotUpToDate: true,
      eventUpToDate: true,
      sessionsInFlowUpToDate: true,
    };
  }, [
    isFlowDetailContext,
    isFlowDetailBasicsContext,
    flowPlan,
    flowForPublish,
    eventSlotForPublish,
    isEventSlotView,
    showSessionsOption,
    flowSpecificSessionsUpToDate,
    librarySessionsUpToDate,
    hasLibrarySessions,
    isSingleFlowEvent,
    eventUpToDate,
    isGridPublicationReady,
    gridPublicationQuery.data,
    gridPublicationQuery.isError,
    canUpdateEvent,
    eventId,
    planFlowId,
  ]);

  const publishState =
    isFlowDetailContext && flowDetailPublishFromHook
      ? flowDetailPublishFromHook
      : isFlowDetailBasicsContext && basicsPublishFromHook
        ? {
            publicationPhase: basicsPublishFromHook.publicationPhase,
            isUpToDate: basicsPublishFromHook.flowBasicsUpToDate,
            needsInitialPublish: basicsPublishFromHook.needsInitialPublish,
            needsRepublish:
              basicsPublishFromHook.publicationPhase === 'ready' && !basicsPublishFromHook.flowBasicsUpToDate,
            flowUpToDate: basicsPublishFromHook.flowBasicsUpToDate,
            eventSlotUpToDate: true,
            sessionsInFlowUpToDate: true,
            eventUpToDate: true,
          }
        : legacyPublishState;

  const publishFormDefaults = useMemo(
    (): PublishFormData => ({
      publishEventInformation: Boolean(isSingleFlowEvent && !eventUpToDate),
      publishFlowTimeWindow: true,
      publishSlotSequence: true,
      publishSessionsInTheFlow: false,
      publishLibrarySessions: false,
      publishFlowSlotSessions: false,
      publishBasicsFlow: isFlowDetailBasicsContext ? !publishState.flowUpToDate : true,
      publishBasicsEventSlot: true,
    }),
    [isSingleFlowEvent, eventUpToDate, isFlowDetailBasicsContext, publishState.flowUpToDate],
  );

  const showPublishModal = isFlowDetailContext || isFlowDetailBasicsContext ? true : !isOrganizeMode(eventMode);
  const isPublishDisabled =
    isFlowDetailContext || isFlowDetailBasicsContext
      ? publishState.isUpToDate
      : isOrganizeMode(eventMode) || publishState.isUpToDate;
  const isButtonDisabled = isPublishDisabled && !isIndicativeOnly;

  const { control, handleSubmit, reset } = useForm<PublishFormData>({
    defaultValues: publishFormDefaults,
  });

  useEffect(() => {
    if (!isOpen) {
      reset(publishFormDefaults);
    }
  }, [isOpen, publishFormDefaults, reset]);
  const formValues = useWatch({ control });
  const isConfirmDisabled = isFlowDetailContext
    ? publishState.publicationPhase === 'loading' ||
      publishState.publicationPhase === 'error' ||
      !formValues ||
      !(
        (formValues.publishSlotSequence &&
          (!publishState.flowUpToDate || (showFlowTimeWindow && !publishState.eventSlotUpToDate))) ||
        (formValues.publishFlowSlotSessions && showSessionsOption && !publishState.sessionsInFlowUpToDate)
      )
    : publishState.publicationPhase === 'loading' ||
      publishState.publicationPhase === 'error' ||
      !formValues ||
      !(
        (isSingleFlowEvent && formValues.publishEventInformation && !eventUpToDate) ||
        (formValues.publishFlowTimeWindow && showFlowTimeWindow && !publishState.eventSlotUpToDate) ||
        (formValues.publishSlotSequence && !publishState.flowUpToDate) ||
        (formValues.publishSessionsInTheFlow && showSessionsOption && !flowSpecificSessionsUpToDate) ||
        (formValues.publishLibrarySessions && showSessionsOption && hasLibrarySessions && !librarySessionsUpToDate)
      );

  const handleClick = (clickEvent: MouseEvent<HTMLButtonElement>) => {
    clickEvent.stopPropagation();
    if (isIndicativeOnly) {
      onIndicativeClick?.();
      return;
    }
    if (publishState.publicationPhase === 'error') {
      const eid = eventPlan?.id ?? event?.id;
      if (eid) {
        void queryClient.invalidateQueries({ queryKey: [PUBLICATION_STATE_QUERY_KEY, eid, 'ORGANIZE'] });
      }
      return;
    }
    if (showPublishModal) {
      setIsOpen(true);
    }
  };

  const handleIndicativeClick = (event: MouseEvent<HTMLSpanElement>) => {
    if (!isIndicativeOnly) return;
    event.stopPropagation();
    onIndicativeClick?.();
  };

  const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsOpen(false);
  };

  const handleBasicsConfirm = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    try {
      const planEventId = eventPlan?.id ?? event?.id;
      if (!planEventId) throw new Error('PLAN event id is required for publishing');
      if (!planFlowId) throw new Error('PLAN flow ID is required for publishing');
      const response = await publishBatchMutation({
        eventId: planEventId,
        body: {
          mode: 'ORGANIZE',
          items: [{ itemType: PUBLICATION_ITEM_TYPE.FLOW_BASIC, flowId: planFlowId }],
        },
      });
      const failed = response.results.filter((r) => r.status === 'failed');
      if (failed.length > 0) {
        const msg =
          failed
            .map((f) => f.errorMessage)
            .filter(Boolean)
            .join('; ') || 'Publish failed';
        throw new ServerError({ message: msg, statusCode: 400 });
      }
      notification.notifySuccess(t('flows.notification.updateSuccess', { title: flowPlan?.title || flowToUse.title }));
      setIsOpen(false);
    } catch (error) {
      const errorMessage = t('flows.notification.updateError', {
        title: flowPlan?.title || flowToUse.title,
      });
      const serverErrorMessage = (error as ServerError).message ?? undefined;
      const errorExplanation = serverErrorMessage ? `: ${serverErrorMessage}` : '';
      notification.notifyError(`${errorMessage}${errorExplanation}`);
    }
  };

  const onSubmit = async (data: PublishFormData) => {
    try {
      const planEventId = eventPlan?.id ?? event?.id;
      if (!planEventId) {
        throw new Error('PLAN event id is required for publishing');
      }

      if (isFlowDetailContext) {
        const publicationReady = flowDetailPublicationQuery.isPublicationStateReady;
        const shouldPublishFlowSchedule = !publishState.flowUpToDate && data.publishSlotSequence;
        const shouldPublishEventSlot =
          showFlowTimeWindow && !publishState.eventSlotUpToDate && data.publishSlotSequence;
        const shouldPublishSlotSessions =
          showSessionsOption && !publishState.sessionsInFlowUpToDate && data.publishFlowSlotSessions;
        const items: BatchPublishItem[] = [];
        if (shouldPublishFlowSchedule) {
          if (!planFlowId) {
            throw new Error('PLAN flow ID is required for publishing');
          }
          items.push({ itemType: PUBLICATION_ITEM_TYPE.FLOW_SLOTS, flowId: planFlowId });
        }
        if (shouldPublishEventSlot) {
          const planSlotId = eventSlotForPublish?.id;
          if (!planSlotId) {
            throw new Error('PLAN event slot id is required for publishing');
          }
          items.push({ itemType: PUBLICATION_ITEM_TYPE.EVENT_SLOT, slotId: planSlotId });
        }
        if (shouldPublishSlotSessions) {
          const pubItems = flowDetailPublicationQuery.data?.items;
          for (const session of uniqueSessionsInFlow) {
            if (sessionNeedsSessionBasicPublish(session, publicationReady, pubItems)) {
              items.push({ itemType: PUBLICATION_ITEM_TYPE.SESSION_BASIC, sessionId: session.id });
            }
          }
        }
        if (items.length === 0) {
          setIsOpen(false);
          return;
        }
        const response = await publishBatchMutation({
          eventId: planEventId,
          body: { mode: 'ORGANIZE', items },
        });
        const failed = response.results.filter((r) => r.status === 'failed');
        if (failed.length > 0) {
          const msg =
            failed
              .map((f) => f.errorMessage)
              .filter(Boolean)
              .join('; ') || 'Publish failed';
          throw new ServerError({ message: msg, statusCode: 400 });
        }
        notification.notifySuccess(
          t('flows.notification.updateSuccess', { title: flowPlan?.title || flowToUse.title }),
        );
        setIsOpen(false);
        return;
      }

      const shouldPublishEvent = isSingleFlowEvent && !eventUpToDate && data.publishEventInformation;
      const shouldPublishFlow = !publishState.flowUpToDate && data.publishSlotSequence;
      const shouldPublishEventSlot =
        showFlowTimeWindow && !publishState.eventSlotUpToDate && data.publishFlowTimeWindow;
      const shouldPublishFlowSessions =
        showSessionsOption && !flowSpecificSessionsUpToDate && data.publishSessionsInTheFlow;
      const shouldPublishLibrarySessions =
        showSessionsOption && hasLibrarySessions && !librarySessionsUpToDate && data.publishLibrarySessions;
      const shouldPublishSessions = shouldPublishFlowSessions || shouldPublishLibrarySessions;
      const shouldSubmit = shouldPublishEvent || shouldPublishFlow || shouldPublishEventSlot || shouldPublishSessions;
      if (!shouldSubmit) {
        setIsOpen(false);
        return;
      }

      const items: BatchPublishItem[] = [];

      const needsEventBasic = isSingleFlowEvent && (shouldPublishEvent || shouldPublishFlow);

      if (needsEventBasic) {
        items.push({ itemType: PUBLICATION_ITEM_TYPE.EVENT_BASIC });
      }

      if (shouldPublishFlow) {
        if (!planFlowId) {
          throw new Error('PLAN flow ID is required for publishing');
        }
        items.push({ itemType: PUBLICATION_ITEM_TYPE.FLOW_SLOTS, flowId: planFlowId });
      }

      if (shouldPublishEventSlot && eventSlotIdToUse) {
        items.push({
          itemType: PUBLICATION_ITEM_TYPE.EVENT_SLOT,
          slotId: eventSlotIdToUse,
        });
      }

      if (shouldPublishSessions) {
        const sessionsToPublish = [
          ...(shouldPublishFlowSessions ? flowSpecificSessions : []),
          ...(shouldPublishLibrarySessions ? librarySessions : []),
        ];
        for (const session of sessionsToPublish) {
          items.push({ itemType: PUBLICATION_ITEM_TYPE.SESSION_BASIC, sessionId: session.id });
        }
      }

      const response = await publishBatchMutation({
        eventId: planEventId,
        body: { mode: 'ORGANIZE', items },
      });

      const failed = response.results.filter((r) => r.status === 'failed');
      if (failed.length > 0) {
        const msg =
          failed
            .map((f) => f.errorMessage)
            .filter(Boolean)
            .join('; ') || 'Publish failed';
        throw new ServerError({ message: msg, statusCode: 400 });
      }

      notification.notifySuccess(t('flows.notification.updateSuccess', { title: flowPlan?.title || flowToUse.title }));
      setIsOpen(false);
    } catch (error) {
      const errorMessage = t('flows.notification.updateError');
      const serverErrorMessage = error instanceof ServerError ? error.message : undefined;
      const errorExplanation = serverErrorMessage ? `: ${serverErrorMessage}` : '';
      notification.notifyError(`${errorMessage}${errorExplanation}`);
    }
  };

  const isPendingAny = isPublishBatchPending;

  const tooltipTitle =
    publishState.publicationPhase === 'loading'
      ? t('common.loading')
      : publishState.publicationPhase === 'error'
        ? t('common.publicationStatusError')
        : isFlowDetailContext || isFlowDetailBasicsContext
          ? !isPlanMode
            ? publishState.isUpToDate
              ? t('common.upToDate')
              : publishState.needsRepublish
                ? t('common.unpublishedUpdates')
                : t('common.publish')
            : isOrganizeMode(modeToUse)
              ? publishState.needsRepublish
                ? t('common.unpublishedUpdates')
                : t('common.notPublishedYet')
              : publishState.needsRepublish
                ? t('common.rePublish')
                : publishState.needsInitialPublish
                  ? t('common.publish')
                  : t('common.upToDate')
          : isOrganizeMode(eventMode)
            ? publishState.needsRepublish
              ? t('common.unpublishedUpdates')
              : t('common.notPublishedYet')
            : publishState.needsRepublish
              ? t('common.rePublish')
              : publishState.needsInitialPublish
                ? t('common.publish')
                : t('common.upToDate');

  // Guard: Require flowPlan in all contexts - no publishing without PLAN data
  if (!flowPlan) {
    return null;
  }

  // In flowDetail / flowDetailBasics context, verify flowPlan matches flowToUse origin
  if (isFlowDetailContext || isFlowDetailBasicsContext) {
    const planOriginId = flowPlan.origin ? getObjectId(flowPlan.origin) : flowPlan.id;
    const flowOriginId = flowToUse.origin ? getObjectId(flowToUse.origin) : flowToUse.id;
    if (planOriginId !== flowOriginId) {
      return null;
    }
  }

  if (isFlowDetailBasicsContext && !basicsPublishFromHook) {
    return null;
  }

  const iconFontSize = buttonOrIcon === 'icon' ? 'medium' : 'small';
  const outlinedSmallLabel = publishState.isUpToDate
    ? t('common.upToDate')
    : publishState.needsRepublish
      ? t('common.rePublish')
      : t('common.publish');
  const renderIcon = () => {
    if (publishState.publicationPhase === 'loading') {
      return (
        <CircularProgress size={buttonOrIcon === 'icon' ? 20 : 14} thickness={5} sx={{ color: 'text.secondary' }} />
      );
    }
    if (publishState.publicationPhase === 'error') {
      return <ErrorOutline color="error" fontSize={iconFontSize} />;
    }
    if (publishState.isUpToDate)
      return <PublishedWithChangesOutlined sx={{ color: 'success.main' }} fontSize={iconFontSize} />;
    if (publishState.needsRepublish) return <PublishedWithChanges fontSize={iconFontSize} />;
    return <Share fontSize={iconFontSize} />;
  };

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span
          style={
            buttonOrIcon === 'outlinedSmall'
              ? { display: 'inline-flex', flexShrink: 0, minWidth: 'max-content' }
              : { display: 'inline-block' }
          }
          onClick={handleIndicativeClick}
        >
          {buttonOrIcon === 'icon' ? (
            <IconButton
              aria-label="publish"
              aria-disabled={isIndicativeOnly || undefined}
              color={publishState.isUpToDate ? 'success' : 'primary'}
              disabled={isButtonDisabled}
              onClick={handleClick}
              loading={isPendingAny}
              sx={{ width: '36px', height: '36px' }}
            >
              {renderIcon()}
            </IconButton>
          ) : buttonOrIcon === 'outlinedSmall' ? (
            <Button
              variant={publishState.isUpToDate ? 'outlined' : 'contained'}
              size="small"
              color={publishState.isUpToDate ? 'success' : 'primary'}
              aria-label={outlinedSmallLabel}
              aria-disabled={isIndicativeOnly || undefined}
              disabled={isButtonDisabled}
              onClick={handleClick}
              loading={isPendingAny}
              endIcon={renderIcon()}
              sx={{
                height: 22,
                minHeight: 22,
                minWidth: 'max-content',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                fontSize: isCompactSlotChrome ? '0.65rem' : '0.75rem',
                px: 1,
              }}
            >
              {outlinedSmallLabel}
            </Button>
          ) : (
            <Button
              variant={publishState.isUpToDate ? 'outlined' : 'contained'}
              color={publishState.isUpToDate ? 'success' : 'primary'}
              aria-disabled={isIndicativeOnly || undefined}
              sx={{
                height: 22,
                ...(publishState.isUpToDate
                  ? {
                      '&.Mui-disabled': {
                        color: 'success.main',
                        borderColor: 'success.main',
                        bgcolor: 'transparent',
                        opacity: 1,
                      },
                      '&.Mui-disabled .MuiSvgIcon-root': {
                        color: 'success.main',
                      },
                    }
                  : {}),
              }}
              startIcon={
                publishState.isUpToDate ? (
                  <CheckCircleOutlined />
                ) : publishState.needsRepublish ? (
                  <PublishedWithChanges />
                ) : (
                  <Share />
                )
              }
              disabled={isButtonDisabled}
              onClick={handleClick}
              loading={isPendingAny}
            >
              {publishState.isUpToDate
                ? t('common.upToDate')
                : publishState.needsRepublish
                  ? t('common.rePublish')
                  : t('common.publish')}
            </Button>
          )}
        </span>
      </Tooltip>
      {isFlowDetailBasicsContext ? (
        <ConfirmationModal
          open={isOpen}
          isLoading={isPendingAny}
          title={t('flows.publishModal.basicsPublish.title')}
          text={t('flows.publishModal.basicsPublish.text', { title: flowToUse.title })}
          onConfirm={handleBasicsConfirm}
          onCancel={handleCancel}
        />
      ) : (
        <Dialog
          onClick={(e) => e.stopPropagation()}
          open={isOpen}
          onClose={handleCancel}
          aria-labelledby="publish-dialog-title"
          aria-describedby="publish-dialog-text"
        >
          <DialogTitle id="publish-dialog-title">
            {isFlowDetailContext ? (
              <Box>
                <Typography variant="h6" component="div">
                  {t('flows.publishModal.schedule.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {flowToUse.title}
                </Typography>
              </Box>
            ) : isSingleFlowEvent ? (
              t('event.publishModal.title')
            ) : (
              t('flows.publishModal.title')
            )}
          </DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              {isFlowDetailContext ? (
                <>
                  <DialogContentText id="publish-dialog-text">
                    {t('flows.publishModal.schedule.description')}
                  </DialogContentText>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ mb: 0.5, fontWeight: 'bold' }}>{t('flows.publishModal.schedule.scopeHeading')}</Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {publishState.flowUpToDate && (!showFlowTimeWindow || publishState.eventSlotUpToDate) ? (
                        <FormControlLabel
                          control={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: 42,
                                height: 42,
                                justifyContent: 'center',
                              }}
                            >
                              <PublishedWithChangesOutlined sx={{ color: 'success.main' }} fontSize="small" />
                            </Box>
                          }
                          label={t('flows.publishModal.schedule.flowSchedule')}
                          disabled
                          sx={{
                            '&.Mui-disabled': { color: 'text.primary' },
                            'alignItems': 'center',
                            'mb': 0.5,
                            '.MuiSvgIcon-root': { color: 'success.main' },
                          }}
                        />
                      ) : (
                        <Controller
                          name="publishSlotSequence"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={<Checkbox checked={field.value} onChange={field.onChange} />}
                              label={t('flows.publishModal.schedule.flowSchedule')}
                              sx={{ alignItems: 'center', mb: 0.5 }}
                            />
                          )}
                        />
                      )}
                      {!showSessionsOption ? (
                        <FormControlLabel
                          control={<Checkbox checked={false} disabled />}
                          label={t('flows.publishModal.schedule.sessionsInFlow')}
                          sx={{ alignItems: 'center' }}
                        />
                      ) : uniqueSessionsInFlow.length === 0 || publishState.sessionsInFlowUpToDate ? (
                        <FormControlLabel
                          control={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: 42,
                                height: 42,
                                justifyContent: 'center',
                              }}
                            >
                              <PublishedWithChangesOutlined sx={{ color: 'success.main' }} fontSize="small" />
                            </Box>
                          }
                          label={t('flows.publishModal.schedule.sessionsInFlow')}
                          disabled
                          sx={{
                            '&.Mui-disabled': { color: 'text.primary' },
                            'alignItems': 'center',
                            '.MuiSvgIcon-root': { color: 'success.main' },
                          }}
                        />
                      ) : (
                        <Controller
                          name="publishFlowSlotSessions"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={<Checkbox checked={field.value} onChange={field.onChange} />}
                              label={t('flows.publishModal.schedule.sessionsInFlow')}
                              sx={{ alignItems: 'center' }}
                            />
                          )}
                        />
                      )}
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <DialogContentText id="publish-dialog-text">
                    {isSingleFlowEvent
                      ? t('event.publishModal.text', { title: event?.title })
                      : t('flows.publishModal.text', { title: flowToUse.title })}
                  </DialogContentText>
                  {isSingleFlowEvent && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                      <DialogContentText variant="body2" color="info.contrastText">
                        {t('event.publishModal.singleFlowEventInfo')}
                      </DialogContentText>
                    </Box>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ mb: 1, fontWeight: 'bold' }}>{`${t('common.publish')}:`}</Box>
                    <Controller
                      name="publishSessionsInTheFlow"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox checked={field.value} onChange={field.onChange} />}
                          label={
                            isSingleFlowEvent
                              ? t('event.publishModal.sessionsInTheEvent')
                              : t('flows.publishModal.sessionsInTheFlow')
                          }
                        />
                      )}
                    />
                    {isSingleFlowEvent && (
                      <FormControlLabel
                        control={<Checkbox checked={true} disabled={true} />}
                        label={t('event.publishModal.flowsInTheEvent')}
                      />
                    )}
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                loading={isPendingAny}
                variant="contained"
                autoFocus
                sx={{ m: '0.4rem' }}
                disabled={isConfirmDisabled}
              >
                {t('common.confirm')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </>
  );
};
